import { Document, Paragraph, TextRun, HeadingLevel, AlignmentType, Packer } from 'docx';
import { saveAs } from 'file-saver';

interface JobData {
  title: string;
  department?: string;
  location?: string;
  employment_type: string;
  remote_option: string;
  description: string;
  responsibilities?: string;
  requirements?: string;
  benefits?: string;
  salary_min?: number;
  salary_max?: number;
  salary_currency?: string;
  salary_period?: string;
}

export const downloadJobAsWord = async (job: JobData) => {
  // Helper function to split text into paragraphs (for description)
  const createParagraphs = (text: string): Paragraph[] => {
    const lines = text.split('\n').filter(line => line.trim());
    return lines.map(line => 
      new Paragraph({
        text: line.trim(),
        alignment: AlignmentType.JUSTIFIED,
        spacing: { after: 100 }
      })
    );
  };

  // Helper function to split text into bullet points
  const createBulletPoints = (text: string): Paragraph[] => {
    const lines = text.split('\n').filter(line => line.trim());
    return lines.map(line => 
      new Paragraph({
        text: line.trim().replace(/^[•\-\*]\s*/, ''),
        bullet: {
          level: 0
        },
        alignment: AlignmentType.JUSTIFIED,
        spacing: { after: 80 }
      })
    );
  };

  const doc = new Document({
    sections: [{
      properties: {
        page: {
          margin: {
            top: 1440,    // 1 inch
            right: 1440,
            bottom: 1440,
            left: 1440,
          },
        },
      },
      children: [
        // Job Title
        new Paragraph({
          text: job.title,
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 300 },
        }),

        // Divider line
        new Paragraph({
          border: {
            bottom: {
              color: "000000",
              space: 1,
              style: "single",
              size: 6,
            },
          },
          spacing: { after: 300 },
        }),

        // Basic Info Section in a clean format
        new Paragraph({
          children: [
            new TextRun({ text: "Location: ", bold: true }),
            new TextRun({ text: job.location || "" }),
          ],
          spacing: { after: 100 },
        }),

        new Paragraph({
          children: [
            new TextRun({ text: "Employment Type: ", bold: true }),
            new TextRun({ text: job.employment_type ? job.employment_type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) : "" }),
          ],
          spacing: { after: 100 },
        }),

        new Paragraph({
          children: [
            new TextRun({ text: "Work Mode: ", bold: true }),
            new TextRun({ 
              text: job.remote_option ? (
                job.remote_option === 'full' ? 'Fully Remote' : 
                job.remote_option === 'hybrid' ? 'Hybrid' : 'On-site'
              ) : "",
            }),
          ],
          spacing: { after: 100 },
        }),

        // Salary
        new Paragraph({
          children: [
            new TextRun({ text: "Salary Range: ", bold: true }),
            new TextRun({
              text: (() => {
                const currency = job.salary_currency || 'USD';
                const min = job.salary_min;
                const max = job.salary_max;
                
                if (min && max) {
                  return `${currency} ${min.toLocaleString()} - ${max.toLocaleString()}`;
                } else if (min) {
                  return `${currency} ${min.toLocaleString()}+`;
                } else if (max) {
                  return `Up to ${currency} ${max.toLocaleString()}`;
                } else {
                  return '';
                }
              })(),
            }),
          ],
          spacing: { after: 250 },
        }),

        // Description
        new Paragraph({
          text: "Job Description",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 150 },
        }),
        ...createParagraphs(job.description),
        new Paragraph({ text: '', spacing: { after: 150 } }),

        // Responsibilities
        ...(job.responsibilities ? [
          new Paragraph({
            text: "Responsibilities",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 150 },
          }),
          ...createBulletPoints(job.responsibilities),
          new Paragraph({ text: '', spacing: { after: 150 } })
        ] : []),

        // Requirements
        ...(job.requirements ? [
          new Paragraph({
            text: "Requirements",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 150 },
          }),
          ...createBulletPoints(job.requirements),
          new Paragraph({ text: '', spacing: { after: 150 } })
        ] : []),

        // Benefits
        ...(job.benefits ? [
          new Paragraph({
            text: "Benefits",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 150 },
          }),
          ...createBulletPoints(job.benefits),
        ] : []),
      ],
    }],
  });

  // Generate and download
  const blob = await Packer.toBlob(doc);
  const fileName = `${job.title.replace(/\s+/g, '_')}_Job_Description.docx`;
  saveAs(blob, fileName);
};